import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
  Request,
  RequestFilter,
  PagedResult,
  RequestDetail,
  ProcessRequestForUluroRequest,
  ProcessRequestForUluroResult,
  UserListItem,
  RequestStatusOption,
  PrintSiteOption,
  PrintLocationDetailOption,
} from '../../core';
import { PrintLocationOption } from '../../core/models/process-request.model';
import { BulkUpdateRequestPayload } from '../../core/models/bulk-update-request.model';
import { RequestFilterOptions } from '../../infrastructure/http/repositories/requests.repository';
import {
  RequestsPageActions,
  RequestsAPIActions,
  RequestDetailPageActions,
} from './requests.actions';
import * as RequestsSelectors from './requests.selectors';

@Injectable({ providedIn: 'root' })
export class RequestsFacade {
  private readonly store = inject(Store);

  // List observables
  readonly requests$: Observable<PagedResult<Request> | null> =
    this.store.select(RequestsSelectors.selectRequests);
  readonly requestItems$: Observable<Request[]> = this.store.select(
    RequestsSelectors.selectRequestItems
  );
  readonly loading$: Observable<boolean> = this.store.select(
    RequestsSelectors.selectRequestsLoading
  );
  readonly error$: Observable<string | null> = this.store.select(
    RequestsSelectors.selectRequestsError
  );
  readonly currentFilter$: Observable<RequestFilter> = this.store.select(
    RequestsSelectors.selectCurrentFilter
  );
  readonly totalCount$: Observable<number> = this.store.select(
    RequestsSelectors.selectTotalCount
  );
  readonly totalPages$: Observable<number> = this.store.select(
    RequestsSelectors.selectTotalPages
  );
  readonly currentPage$: Observable<number> = this.store.select(
    RequestsSelectors.selectCurrentPage
  );
  readonly pageSize$: Observable<number> = this.store.select(
    RequestsSelectors.selectPageSize
  );

  // Filter options
  readonly filterOptions$: Observable<RequestFilterOptions | null> =
    this.store.select(RequestsSelectors.selectFilterOptions);
  readonly filterOptionsLoading$: Observable<boolean> = this.store.select(
    RequestsSelectors.selectFilterOptionsLoading
  );

  // Request detail observables
  readonly currentRequest$: Observable<RequestDetail | null> =
    this.store.select(RequestsSelectors.selectCurrentRequest);
  readonly currentRequestLoading$: Observable<boolean> = this.store.select(
    RequestsSelectors.selectCurrentRequestLoading
  );
  readonly currentRequestError$: Observable<string | null> = this.store.select(
    RequestsSelectors.selectCurrentRequestError
  );
  readonly currentRequestComments$: Observable<string> = this.store.select(
    RequestsSelectors.selectCurrentRequestComments
  );

  // Print locations observables (for process modal)
  readonly printLocations$: Observable<PrintLocationOption[]> =
    this.store.select(RequestsSelectors.selectPrintLocations);
  readonly printLocationsLoading$: Observable<boolean> = this.store.select(
    RequestsSelectors.selectPrintLocationsLoading
  );

  // Print locations by site observables (for request details)
  readonly printLocationsBySite$: Observable<PrintLocationDetailOption[]> =
    this.store.select(RequestsSelectors.selectPrintLocationsBySite);
  readonly printLocationsBySiteLoading$: Observable<boolean> =
    this.store.select(RequestsSelectors.selectPrintLocationsBySiteLoading);

  // User list observables
  readonly userList$: Observable<UserListItem[]> = this.store.select(
    RequestsSelectors.selectUserList
  );
  readonly userListLoading$: Observable<boolean> = this.store.select(
    RequestsSelectors.selectUserListLoading
  );

  // Request statuses observables
  readonly requestStatuses$: Observable<RequestStatusOption[]> =
    this.store.select(RequestsSelectors.selectRequestStatuses);
  readonly requestStatusesLoading$: Observable<boolean> = this.store.select(
    RequestsSelectors.selectRequestStatusesLoading
  );

  // Print sites observables
  readonly printSites$: Observable<PrintSiteOption[]> = this.store.select(
    RequestsSelectors.selectPrintSites
  );
  readonly printSitesLoading$: Observable<boolean> = this.store.select(
    RequestsSelectors.selectPrintSitesLoading
  );

  // Update request observables
  readonly updateRequestLoading$: Observable<boolean> = this.store.select(
    RequestsSelectors.selectUpdateRequestLoading
  );
  readonly updateRequestError$: Observable<string | null> = this.store.select(
    RequestsSelectors.selectUpdateRequestError
  );

  // Process request observables
  readonly processRequestLoading$: Observable<boolean> = this.store.select(
    RequestsSelectors.selectProcessRequestLoading
  );
  readonly processRequestError$: Observable<string | null> = this.store.select(
    RequestsSelectors.selectProcessRequestError
  );
  readonly processRequestResult$: Observable<ProcessRequestForUluroResult | null> =
    this.store.select(RequestsSelectors.selectProcessRequestResult);

  // Actions
  loadRequests(filter?: Partial<RequestFilter>): void {
    this.store.dispatch(RequestsPageActions.loadRequests({ filter }));
  }

  search(searchTerm: string): void {
    this.store.dispatch(RequestsPageActions.search({ searchTerm }));
  }

  sort(sortField: string): void {
    this.store.dispatch(RequestsPageActions.sort({ sortField }));
  }

  changePage(page: number): void {
    this.store.dispatch(RequestsPageActions.changePage({ page }));
  }

  changePageSize(pageSize: number): void {
    this.store.dispatch(RequestsPageActions.changePageSize({ pageSize }));
  }

  applyFilters(filters: Partial<RequestFilter>): void {
    this.store.dispatch(RequestsPageActions.applyFilters({ filters }));
  }

  clearFilters(): void {
    this.store.dispatch(RequestsPageActions.clearFilters());
  }

  refresh(): void {
    this.store.dispatch(RequestsPageActions.refresh());
  }

  loadFilterOptions(): void {
    this.store.dispatch(RequestsAPIActions.loadFilterOptions());
  }

  loadRequestDetail(requestNumber: number): void {
    this.store.dispatch(
      RequestDetailPageActions.loadRequestDetail({ requestNumber })
    );
  }

  updateRequest(
    requestNumber: number,
    updates: {
      printSiteId?: number | null;
      countryCode?: string | null;
      shippingMethods?: string | null;
      isManualCheck?: boolean | null;
      sla?: boolean | null;
      contactName?: string | null;
      contactEmails?: string | null;
      contactPhone?: string | null;
      isSlaFeeWaived?: boolean | null;
      isSpecialHandling?: boolean | null;
      isGarnCorp?: boolean | null;
      newComments?: string | null;
      requestStatusId?: number | null;
      assignedUserId?: number | null;
      assignedByUserId?: number | null;
    },
    modifiedBy: string
  ): void {
    this.store.dispatch(
      RequestDetailPageActions.updateRequest({
        requestNumber,
        updates,
        modifiedBy,
      })
    );
  }

  updateRequestStatus(
    requestNumber: number,
    statusId: number,
    modifiedBy: string
  ): void {
    this.store.dispatch(
      RequestDetailPageActions.updateRequestStatus({
        requestNumber,
        statusId,
        modifiedBy,
      })
    );
  }

  assignRequest(
    requestNumber: number,
    assignedUserId: number,
    assignedByUserId: number,
    modifiedBy: string
  ): void {
    this.store.dispatch(
      RequestDetailPageActions.assignRequest({
        requestNumber,
        assignedUserId,
        assignedByUserId,
        modifiedBy,
      })
    );
  }

  processRequest(
    requestNumber: number,
    request: ProcessRequestForUluroRequest
  ): void {
    this.store.dispatch(
      RequestDetailPageActions.processRequest({ requestNumber, request })
    );
  }

  loadPrintLocations(): void {
    this.store.dispatch(RequestDetailPageActions.loadPrintLocations());
  }

  loadPrintLocationsBySite(printSiteId: number): void {
    this.store.dispatch(
      RequestDetailPageActions.loadPrintLocationsBySite({ printSiteId })
    );
  }

  loadUserList(): void {
    this.store.dispatch(RequestDetailPageActions.loadUserList());
  }

  loadRequestStatuses(): void {
    this.store.dispatch(RequestDetailPageActions.loadRequestStatuses());
  }

  loadPrintSites(): void {
    this.store.dispatch(RequestDetailPageActions.loadPrintSites());
  }

  clearCurrentRequest(): void {
    this.store.dispatch(RequestDetailPageActions.clearCurrentRequest());
  }

  bulkUpdateRequests(payload: BulkUpdateRequestPayload): void {
    this.store.dispatch(RequestsPageActions.bulkUpdateRequests({ payload }));
  }
}
